import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import { Fragment, useCallback, useEffect, useState } from "react";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import { GlobalSearchBarProps, Option } from "./types";
import { Box, Button, useMediaQuery, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { responsiveAppBarStyles } from "./styles";
import { handleGlobalSearch } from "../../services/globalSearchService";
import Mixpanel from "../../utils/mixpanel";
import { APP_COLOR, BORDER_COLOR } from "../../theme";
import { ENTERPRISE_IMAGE_PATH, NAVBAR_FOOTER_TEXT_COLOR } from "../../theme";
import { isEnterprise } from "../../theme";

export const GlobalSearchBar: React.FC<GlobalSearchBarProps> = ({
  userId,
  allowDisplaySearchBar,
  setAllowDisplaySearchBar,
  isArtistSearchBar,
}) => {
  const [open, setOpen] = useState(false);
  const [queryValue, setQueryValue] = useState<string | null>(null);
  const [options, setOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  const { getValidAccessToken } = useGetValidAccessToken();
  const isSmallScreen = useMediaQuery("(max-width:600px)");
  const isMediumScreen = useMediaQuery("(max-width: 1536px)");
  const navigate = useNavigate();
  const theme = useTheme();
  const isExtraLargeScreen = useMediaQuery(theme.breakpoints.down("xl"));

  const handleQueryChange = async (e: string) => {
    setQueryValue(e);
    if (e.length === 0) {
      setQueryValue(null);
      setLoading(false);
      setOptions([]);
    } else {
      const accessToken = await getValidAccessToken();
      setLoading(true);
      setTimeout(async () => {
        if (queryValue !== "" && userId) {
          Mixpanel.track("global_search_bar_query");
          const response = await handleGlobalSearch(
            accessToken,
            userId,
            queryValue!
          );
          if (response) setLoading(false);
          const newTrackListArray = response.getTracksList().map((el) => {
            return {
              title: el.getTitle(),
              group: response.getTracksList().length > 1 ? "Tracks" : "Track",
              image: el.getImageUrl(),
              moreDetails: el,
              id: el.getId(),
            };
          });
          const newTrackFilesListArray = response
            .getTrackFilesList()
            .map((el) => {
              return {
                title: el.getFilename(),
                group:
                  response.getTrackFilesList().length > 1
                    ? "Track files"
                    : "Track file",
                image: isEnterprise
                  ? ENTERPRISE_IMAGE_PATH
                  : "/src/ts/app/public/assets/images/logo512.png",
                moreDetails: el,
                id: el.getId(),
              };
            });
          const newUsersListArray = response.getUsersList().map((el) => {
            return {
              title: el.getUsername(),
              group: response.getUsersList().length > 1 ? "Users" : "User",
              image: el.getProfilePicture(),
              moreDetails: el,
              id: el.getUserId(),
            };
          });
          if (isArtistSearchBar) {
            setOptions(newUsersListArray);
          } else {
            setOptions([
              ...newTrackListArray,
              ...newTrackFilesListArray,
              ...newUsersListArray,
            ]);
          }
        } else {
          setLoading(false);
          setOptions([]);
        }
      }, 1000);
    }
  };

  const handleSearchBarExpansion = () => {
    setAllowDisplaySearchBar(true);
  };

  const handleNavigation = (option: any) => {
    if (option.group.includes("Track")) {
      const id = option.moreDetails.getId();
      navigate(`/track/${id}`);
    } else if (option.group.includes("User")) {
      const userName = option.moreDetails.getUsername();
      navigate(`/user-profile/${userName}`);
    }
  };

  const handleClick = useCallback((e: any) => {
    if (e.target.localName !== "input" && e.target.localName !== "svg") {
      setOptions([]);
    }
  }, []);

  useEffect(() => {
    document && document.addEventListener("click", handleClick);
    return () => {
      document && document.removeEventListener("click", handleClick);
    };
  }, [handleClick]);

  useEffect(() => {
    const keyDownHandler = (event: any) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setQueryValue(null);
        setAllowDisplaySearchBar(false);
      }
    };
    document && document.addEventListener("keydown", keyDownHandler);
    return () => {
      document && document.removeEventListener("keydown", keyDownHandler);
    };
  }, [setAllowDisplaySearchBar, setQueryValue]);

  return (
    <>
      <Box id="step-3" onClick={handleSearchBarExpansion}>
        <Autocomplete
          fullWidth
          size={isExtraLargeScreen ? "small" : "medium"}
          PaperComponent={({ children, ...rest }) => (
            <div
              style={{
                display: options.length === 0 ? "none" : "block",
              }}
              {...rest}
            >
              {children}
            </div>
          )}
          id="asynchronous-demo"
          sx={{
            ...responsiveAppBarStyles.autoComplete,
            ...{
              minWidth: isSmallScreen ? "90vw" : "250px",
              "& .MuiOutlinedInput-root": {
                border: "none",
                "& fieldset": {
                  border: `1px solid ${BORDER_COLOR}`,
                },
              },
            },
          }}
          open={open}
          noOptionsText={"No results found"}
          clearOnEscape={true}
          onOpen={() => {
            if (userId) {
              Mixpanel.track("global_search_bar_open");
            }
            setOpen(true);
          }}
          onClose={() => {
            setOpen(false);
            // clear();
            setAllowDisplaySearchBar(false);
          }}
          isOptionEqualToValue={(option, value) => option.title === value.title}
          getOptionLabel={(option) => option.title}
          groupBy={(option) => option.group}
          options={options}
          loading={loading}
          renderOption={(_props, option) => (
            <Box sx={{ border: `1px solid ${BORDER_COLOR}` }} key={option.id}>
              <Box
                onClick={() => handleNavigation(option)}
                sx={responsiveAppBarStyles.searchContainer}
              >
                <img
                  src={
                    option.image.includes("http")
                      ? option.image
                      : isEnterprise
                      ? ENTERPRISE_IMAGE_PATH
                      : `/assets/images/logo512.png`
                  }
                  alt={""}
                  style={responsiveAppBarStyles.searchImage}
                />
                <Box sx={responsiveAppBarStyles.titleContainer}>
                  {option.title}
                </Box>
              </Box>
            </Box>
          )}
          renderInput={(params) => (
            <>
              <TextField
                {...params}
                value={queryValue || ""}
                placeholder={
                  allowDisplaySearchBar && !isArtistSearchBar
                    ? "Search Tracks and Artists"
                    : "Search Artists"
                }
                sx={{
                  "& input": {
                    color: NAVBAR_FOOTER_TEXT_COLOR,
                  },
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: APP_COLOR,
                      boxShadow:
                        "0px 1px 2px 0px rgba(16, 24, 40, 0.05), 0px 0px 0px 4px rgba(158, 119, 237, 0.24)",
                    },
                  },
                  position: "relative",
                  caretColor: !allowDisplaySearchBar ? "transparent" : "none",
                  marginRight: "10px",
                  width: isSmallScreen ? "100%" : "320px",
                  borderRadius: "var(--radius-md, 8px)",
                  [theme.breakpoints.down("xl")]: {
                    "& input": {
                      height: "10px",
                    },
                  },
                  [theme.breakpoints.down("md")]: {
                    "& input": {
                      height: "auto",
                    },
                  },
                }}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    e.preventDefault();
                    handleQueryChange("");
                    setAllowDisplaySearchBar(false);
                  }
                }}
                onChange={(e) => {
                  handleQueryChange(e.target.value);
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <Fragment>
                      {loading ? (
                        <CircularProgress
                          sx={{ color: NAVBAR_FOOTER_TEXT_COLOR }}
                          color="inherit"
                          size={20}
                        />
                      ) : (
                        <Button
                          style={{ marginRight: -20 }}
                          sx={
                            isMediumScreen
                              ? {
                                  ...responsiveAppBarStyles.signupButton,
                                  ...{
                                    fontSize: "12px",
                                    height: "30px",
                                  },
                                }
                              : responsiveAppBarStyles.signupButton
                          }
                        >
                          Search
                        </Button>
                      )}
                      {params.InputProps.endAdornment}
                    </Fragment>
                  ),
                }}
              />
            </>
          )}
        />
      </Box>
    </>
  );
};